import { IStyle, Stack } from '@fluentui/react';
import { Loader, useClassNames } from '@h2oai/ui-kit';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { useRoles } from '../../authz/providers/RoleProvider';
import { stackStylesPage } from '../../themes/themes';
import { ClassNamesFromIStyles } from '../../utils/models';

interface INavigationWrapperStyles {
  container: IStyle;
  stack: IStyle;
  noPermissions: IStyle;
}

const navigationWrapperStyles = (): Partial<INavigationWrapperStyles> => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 0,
  },
  noPermissions: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'pre-line',
    textAlign: 'center',
  },
});

const NavigationWrapper: React.FC = ({ children }) => {
  const { permissions, loadingPermissions } = useRoles(),
    history = useHistory(),
    classNames = useClassNames<INavigationWrapperStyles, ClassNamesFromIStyles<INavigationWrapperStyles>>(
      'navigationWrapper',
      navigationWrapperStyles
    ),
    [loading, setLoading] = React.useState(true),
    [showLoader, setShowLoader] = React.useState<boolean>(false),
    [restrictAccess, setRestrictAccess] = React.useState(true);

  React.useEffect(() => {
    if (!loadingPermissions) {
      const pathsPermissions = [
        { path: 'accessControl', permission: permissions?.orchestrator?.canViewAccessControl },
        { path: 'workflows', permission: permissions?.orchestrator?.canViewWorkflows },
        { path: 'executorPools', permission: permissions?.orchestrator?.canViewRunnables },
        { path: 'runnables', permission: permissions?.orchestrator?.canViewRunnables },
        { path: 'activeExecutions', permission: permissions?.orchestrator?.canViewWorkflows },
        { path: 'executions', permission: permissions?.orchestrator?.canViewWorkflows },
      ];

      const restrict = pathsPermissions.some(
        ({ path, permission }) => history.location.pathname?.includes(path) && !permission
      );

      setRestrictAccess(restrict);
      setLoading(false);
    }
  }, [history.location.pathname, loadingPermissions, permissions?.orchestrator]);

  React.useEffect(() => {
    if (loading) {
      const timerId = window.setTimeout(() => setShowLoader(true), 1000);
      return () => window.clearTimeout(timerId);
    } else {
      setShowLoader(false);
      return undefined;
    }
  }, [loading]);

  return (
    <div className={classNames.container}>
      <Stack styles={stackStylesPage} className={classNames.stack}>
        {restrictAccess ? (
          <div className={classNames.noPermissions}>
            {loading ? (
              showLoader ? (
                <Loader label="Just a moment, checking your permissions" />
              ) : null
            ) : (
              `You have no access to this page.\nAsk the workspace owner to grant you permissions.`
            )}
          </div>
        ) : (
          children
        )}
      </Stack>
    </div>
  );
};

export default NavigationWrapper;
